import type { UserCredential, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { getFirebaseAuth } from './firebase';

type AuthModule = typeof import('firebase/auth');

let authModulePromise: Promise<AuthModule> | null = null;
const loadAuthModule = (): Promise<AuthModule> => {
  if (!authModulePromise) {
    authModulePromise = import('firebase/auth');
  }
  return authModulePromise;
};

const loadAuthDependencies = async () => {
  const [module, auth] = await Promise.all([loadAuthModule(), getFirebaseAuth()]);
  return { module, auth };
};

let googleProvider: GoogleAuthProvider | null = null;
const ensureGoogleProvider = (module: AuthModule): GoogleAuthProvider => {
  if (!googleProvider) {
    googleProvider = new module.GoogleAuthProvider();
    googleProvider.setCustomParameters({ prompt: 'select_account' });
  }
  return googleProvider;
};

let microsoftProvider: OAuthProvider | null = null;
const ensureMicrosoftProvider = (module: AuthModule): OAuthProvider => {
  if (!microsoftProvider) {
    microsoftProvider = new module.OAuthProvider('microsoft.com');
    microsoftProvider.setCustomParameters({ prompt: 'consent' });
    microsoftProvider.addScope('User.Read');
    microsoftProvider.addScope('offline_access');
  }
  return microsoftProvider;
};

export const signInWithGoogle = async (): Promise<UserCredential> => {
  const { module, auth } = await loadAuthDependencies();
  const provider = ensureGoogleProvider(module);
  return module.signInWithPopup(auth, provider);
};

export const signInWithMicrosoft = async (): Promise<UserCredential> => {
  const { module, auth } = await loadAuthDependencies();
  const provider = ensureMicrosoftProvider(module);
  return module.signInWithPopup(auth, provider);
};

export const signInWithEmail = async (email: string, password: string) => {
  const { module, auth } = await loadAuthDependencies();
  return module.signInWithEmailAndPassword(auth, email, password);
};

export const signUpWithEmail = async (email: string, password: string) => {
  const { module, auth } = await loadAuthDependencies();
  return module.createUserWithEmailAndPassword(auth, email, password);
};

export const signOutUser = async () => {
  const { module, auth } = await loadAuthDependencies();
  return module.signOut(auth);
};

export const sendPasswordReset = async (email: string) => {
  const { module, auth } = await loadAuthDependencies();
  return module.sendPasswordResetEmail(auth, email);
};
